import { css } from '@emotion/react';
import { styled } from '@mui/material/styles';
import { useAtomValue } from 'jotai';
import { Group, Panel } from 'react-resizable-panels';
import { Suspense } from 'react';
import { Outlet, useLocation } from 'react-router';
import { GenericFallbacksWrapper } from './components/Fallback/GenericFallbacksWrapper';
import { AssistantSocket } from './components/ChatBotSection/AssistantSocket';
import { ChatBotSection, preloadChatBot } from './components/ChatBotSection/ChatBotSection';
import { Header } from './components/Layout/Header/Header';
import { Header2 } from './components/Layout/Header/Header2';
import { Navbar } from './components/Layout/Navbar/Navbar';
import { BootAwareFallback } from './components/Loader/BootLoader';
import { LoadingBarWhileMounted } from './components/LoadingBar/LoadingBarWhileMounted';
import { ROUTES } from './constants/RouteNameMapping';
import { ContainerName, ZINDEX } from './constants/styles';
import { useFdSearchParams } from './hooks/useFdSearchParams';
import { useIsFundAdmin } from './hooks/useIsFundAdmin';
import { isClosedToFundAdmin } from './navigation/fundAdminSections';
import { usePortfolioCurrencyScope } from './hooks/usePortfolioCurrencyScope';
import { isDealFormOpenState } from './components/Layout/Navbar/AddModal';
import { isPresentModeState, selectedDealActionState } from './pages/DealFlow2/state/DealboardUIStateJ';
import { PermissionKey, PersonaKey } from './services/PermissionAndRolesKeys';
import { PermissionService } from './services/PermissionService';
import { customFieldsByEntityAtom } from './services/state/AdminPanel/CustomFieldsStateJ';
import { LoadingId } from './types';
import { lazyShell, Preload } from './util/lazyPage';

// Section navs, the deal modals and the entity header actions only matter after a click into their
// section, so they load with it instead of in the boot bundle. The slots they render into already
// provide a Suspense fallback.
const AdminPanelNavigation = lazyShell(
  () => import('./components/Layout/Navbar/Navigation/AdminPanel/AdminPanelNavigation'),
  'AdminPanelNavigation'
);
const CPNavigation = lazyShell(
  () => import('./components/Layout/Navbar/Navigation/CompanyProfiles/CPNavigation'),
  'CPNavigation'
);
const DealflowNavigation = lazyShell(
  () => import('./components/Layout/Navbar/Navigation/Dealflow/DealflowNavigation'),
  'DealflowNavigation'
);
const EntityManagementNavigation = lazyShell(
  () => import('./components/Layout/Navbar/Navigation/EntityManagement/EntityManagementNavigation'),
  'EntityManagementNavigation'
);
const FinanceNavigation = lazyShell(
  () => import('./components/Layout/Navbar/Navigation/Finance/FinanceNavigation'),
  'FinanceNavigation'
);
const KPINavigation = lazyShell(
  () => import('./components/Layout/Navbar/Navigation/KPI/KPINavigation'),
  'KPINavigation'
);
const PortfolioReportingNavigation = lazyShell(
  () => import('./components/Layout/Navbar/Navigation/PortfolioReporting/PortfolioReportingNavigation'),
  'PortfolioReportingNavigation'
);
const SourcingNavigation = lazyShell(
  () => import('./components/Layout/Navbar/Navigation/Sourcing/SourcingNavigation'),
  'SourcingNavigation'
);
const EntityManagementHeaderActions = lazyShell(
  () => import('./pages/EntityManagement/components/EntityManagementHeaderActions'),
  'EntityManagementHeaderActions'
);
const AddDealFormModal = lazyShell(
  () => import('./pages/DealFlow2/Forms/AddDealFormModal'),
  'AddDealFormModal'
);
const DealActionModals = lazyShell(
  () => import('./pages/DealFlow2/modals/DealActionModals'),
  'DealActionModals'
);

const StyledWrapper = css`
  min-height: 0;
  display: grid;
  height: 100%;
  width: 100%;
  grid-template-columns: min-content 1fr min-content; /* additional side nav | content | chatbot drawer */
  &.present-mode {
    grid-template-columns: 1fr;
  }
`;

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  flex-grow: 1;
`;

const HeaderContainer = styled('div')`
  container-type: inline-size;
  container-name: ${ContainerName.headerContainer};
  position: relative;
  z-index: ${ZINDEX.APP_HEADER};
`;

const ChildrenContainer = styled('div')`
  display: flex;
  overflow: auto;
  min-height: 0;
  flex: 1;
  background: ${({ theme }) => theme.gradients.primary};
  background-size: cover;
`;

const MenuWrapper = styled('div')<{ isHidden: boolean }>`
  display: grid;
  grid-template-columns: 72px min-content;
  ${({ isHidden: hidden }) => hidden && 'display: none;'}
`;

export function NavWrapper() {
  const { pathname } = useLocation();
  const isFundAdmin = useIsFundAdmin();
  const innerComponent = getInnerNavComponent(pathname, isFundAdmin);
  const innerHeaderMiddleSlot = getHeaderChildrenByLocation(pathname, isFundAdmin);
  const presentModeOn = useAtomValue(isPresentModeState);
  const { noCpNav } = useFdSearchParams();
  usePortfolioCurrencyScope();
  const isAuditor = PermissionService.get().isAuditor();
  const showHeader =
    !pathname.includes(ROUTES.BULK_UPLOAD_REPORT) &&
    !PermissionService.get().hasPersona(PersonaKey.managedServices) &&
    !isAuditor;

  return (
    <>
      <div css={StyledWrapper} className={presentModeOn ? 'present-mode' : ''}>
        <MenuWrapper isHidden={presentModeOn}>
          <Navbar />
          {/* At boot the section nav's data holds the shell, so both sidebars appear together. */}
          <GenericFallbacksWrapper suspenseFallback={<BootAwareFallback />}>
            {innerComponent}
          </GenericFallbacksWrapper>
        </MenuWrapper>
        <Group id='main-layout' style={{ minHeight: '0' }}>
          <Panel id='main-content' defaultSize={'100%'}>
            <GenericFallbacksWrapper>
              <Container>
                {!noCpNav && showHeader && (
                  <HeaderContainer>
                    <Header>{innerHeaderMiddleSlot}</Header>
                  </HeaderContainer>
                )}

                <ChildrenContainer>
                  <Outlet />
                </ChildrenContainer>
              </Container>
            </GenericFallbacksWrapper>
          </Panel>
          <ChatBotSection />
        </Group>
      </div>
      <AssistantSocket />
      <DealModalsWhenOpen canEditDeal={PermissionService.get().hasPermission(PermissionKey.canEditDeal)} />
    </>
  );
}

function getHeaderChildrenByLocation(location: string, isFundAdmin: boolean) {
  const isKPITemplatesLocation = location.includes(`${ROUTES.KPI}/${ROUTES.KPI_TEMPLATES}`);

  if (isKPITemplatesLocation) {
    return null;
  }

  // Entity Management puts its own actions on the header row next to the breadcrumb,
  // in place of the global search, matching the module's design. Compared by segment
  // so a future '/entities-something' route is not swept up by a prefix match.
  if (location.split('/')[1] === ROUTES.ENTITIES && !isFundAdmin) {
    return (
      <Suspense fallback={null}>
        <EntityManagementHeaderActions />
      </Suspense>
    );
  }

  return <Header2 />;
}

// Modals mount only while open (both already render nothing when closed), so their code is not
// fetched until the first deal action.
export function DealModalsWhenOpen({ canEditDeal }: { canEditDeal: boolean }) {
  // The always-mounted form made the shell wait for custom fields, and some grids read them
  // without suspending, so the shell keeps waiting for them here.
  useAtomValue(customFieldsByEntityAtom);

  return (
    <Suspense fallback={<LoadingBarWhileMounted id={LoadingId.loadDealModal} />}>
      <DealModals canEditDeal={canEditDeal} />
    </Suspense>
  );
}

function DealModals({ canEditDeal }: { canEditDeal: boolean }) {
  const dealFormOpen = useAtomValue(isDealFormOpenState);
  const dealAction = useAtomValue(selectedDealActionState);

  return (
    <>
      {dealFormOpen && <AddDealFormModal />}
      {canEditDeal && dealAction && <DealActionModals />}
    </>
  );
}

function navForLocation(location: string) {
  const loc = location.split('/')[1];

  switch (loc) {
    case ROUTES.PORTFOLIO:
      return PortfolioReportingNavigation;
    case ROUTES.DEAL_FLOW:
      return DealflowNavigation;
    case ROUTES.ADMIN_PANEL:
      return AdminPanelNavigation;
    case ROUTES.KPI:
      return KPINavigation;
    case ROUTES.COMPANIES:
      return CPNavigation;
    case ROUTES.FINANCE2:
      return FinanceNavigation;
    case ROUTES.ENTITIES:
      return EntityManagementNavigation;
    case ROUTES.SOURCING:
      return location.includes(`/${ROUTES.SOURCING}/${ROUTES.SOURCING_PROFILE}/`) ? null : SourcingNavigation;
    default:
      return null;
  }
}

/** Starts downloading the nav the landing route needs, alongside the boot requests. */
export function preloadShellForPath(pathname: string) {
  // As with the pages, the render reports a failure; the head start stays quiet.
  void navForLocation(pathname)
    ?.preload()
    .catch(() => undefined);
  if (pathname.split('/')[1] === ROUTES.ENTITIES) {
    void EntityManagementHeaderActions.preload().catch(() => undefined);
  }
}

/** What any page can open; the warm-up fetches these before the other sections. */
export function shellPreloads(): Preload[] {
  const preloads = [AddDealFormModal.preload, preloadChatBot];
  if (PermissionService.get().hasPermission(PermissionKey.canEditDeal)) {
    preloads.push(DealActionModals.preload);
  }
  return preloads;
}

function getInnerNavComponent(location: string, isFundAdmin: boolean) {
  if (PermissionService.get().hasPersona(PersonaKey.managedServices)) {
    return null;
  }
  // A closed module's menu reads that module's data
  if (isFundAdmin && isClosedToFundAdmin(location)) {
    return null;
  }

  const Nav = navForLocation(location);
  return Nav ? <Nav /> : null;
}
